import type { Dispatch, FC, SetStateAction } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { doRequest } from '@r1-frontend/do-request';
import {
    requestByConnection,
    RequestDto,
    ResponseDto,
} from '@r1-frontend/api-domru/api-request/v1/user/request-by-connection';

import { btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { PolicyNote } from '@r1-frontend/ui-react/components/PolicyNote';
import { ACTIONS } from '@r1-frontend/shared/constants/analytics';
import { EProductsId } from '@r1-frontend/shared/types/EProductsId';

import PopupResult from '~/src/components/Popups/PopupResult';
import { POLICY_PAGE_LINK } from '~/src/constants';
import {
    getPopupResultData,
    initialPopupState,
    STATE_DEFAULT_INPUTS,
} from '~/src/features/videocontrol/pageBlocks/Cart/constants';
import { getLabelsCameras, getResultData } from '~/src/features/videocontrol/pageBlocks/Cart/FeedbackForm/utils';
import type { TField } from '~/src/features/videocontrol/pageBlocks/Cart/types';
import { IProps } from '~/src/features/videocontrol/pageBlocks/Cart/types';
import { selectTotalPrice } from '~/src/features/videocontrol/Provider/selectors';
import { useRequisitionContext } from '~/src/features/videocontrol/Provider/useRequisitionContext';
import { dataLayerForCBPhonePush, IDataLayerPushUid } from '~/src/helpers/eventsForDataLayer';
import { selectCsrf } from '~/src/store/selectors/auth/selectCsrf';

import * as ST from '~/src/features/videocontrol/pageBlocks/Cart/FeedbackForm/styled';

export const FeedbackForm: FC<IProps> = ({ isAuth, agreement }) => {
    const csrf = useSelector(selectCsrf);
    const [phone, setPhone] = useState(STATE_DEFAULT_INPUTS);
    const [name, setName] = useState(STATE_DEFAULT_INPUTS);
    const [isValid, setValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [popup, setPopup] = useState(initialPopupState);
    const [beenStarted, setDataLayer] = useState(false);

    const { setResetAllDispatch, state, cameras, plan, installLocation, planOption } = useRequisitionContext();
    const totalPrice = selectTotalPrice(state);

    const getCamera = useMemo(() => (key: number) => {
        return state.cameras[Object.keys(state.cameras)[key] as unknown as number];
    }, [state.cameras]);

    const labelsCameras = useMemo(() => getLabelsCameras(cameras, getCamera), [cameras, getCamera]);

    const onChangeHandlerInput = (stateInput: TField, _setState: Dispatch<SetStateAction<TField>>) => {
        if (!beenStarted && stateInput.value) {
            dataLayerForCBPhonePush({
                action: ACTIONS.started,
                option: `${installLocation?.type || null} | ${labelsCameras}| ${plan?.name || null} | ${planOption?.name || null} | ${totalPrice}₽`,
                uid: isAuth ? agreement : null,
            } as unknown as IDataLayerPushUid);
            setDataLayer(true);
        }
        _setState(stateInput);
    };

    useEffect(() => {
        setValid(name.isValid && phone.isValid);
    }, [phone, name, cameras, plan]);

    const closePopup = useCallback(() => {
        name.clearInput();
        phone.clearInput();
        setPopup((prevState) => ({
            ...prevState,
            isResultOpened: false,
        }));
    }, [name, phone]);

    const openPopupResult = (result: boolean, message: string) => {
        dataLayerForCBPhonePush({
            action: result ? ACTIONS.finished : ACTIONS.error,
            option: `${installLocation?.type || null} | ${labelsCameras}| ${plan?.name || null} | ${planOption?.name || null} | ${totalPrice}₽`,
            uid: isAuth ? agreement : null,
        } as unknown as IDataLayerPushUid);
        setPopup({ ...getPopupResultData({ result, message }) });
    };

    const onSubmit = async() => {
        const result = getResultData(state, getCamera, cameras);
        const params: RequestDto = {
            fio: name.value,
            phone: phone.value,
            csrf,
            product_id: [EProductsId.videoControl],
            check_call_type: window && window.location.href,
            cameras: result,
        };
        setIsLoading(true);

        const resp = await doRequest.apiRequest<ResponseDto>(requestByConnection(params));
        if (resp.isSuccess) {
            const { status, message } = resp.payload;
            const ok = !!status;

            openPopupResult(ok, message);
        } else {
            openPopupResult(false, 'Не удалось отправить заявку. Пожалуйста, попробуйте еще раз.');
        }

        setIsLoading(false);
        setValid(false);
        setResetAllDispatch();
    };

    const buttonText = 'Оставить заявку';

    return (
        <ST.FormContainer>
            <ST.TitleWrap>
                <ST.Title>Введите ваши контактные данные.</ST.Title>
                <ST.SubText>
                    Нажмите кнопку «Оставить заявку», мы перезвоним и уточним детали подключения.
                </ST.SubText>
            </ST.TitleWrap>
            <ST.InputsWrap>
                <ST.InputName
                    onChange={(stateInput: TField) => onChangeHandlerInput(stateInput, setName)}
                    type='name'
                    errorMessage='Должны быть только буквы'
                    placeholder='Ваше имя'
                    name='name'
                    className={'ym-disable-keys'}
                />
                <ST.InputPhone
                    onChange={(stateInput: TField) => onChangeHandlerInput(stateInput, setPhone)}
                    type='phone'
                    errorMessage='Укажите корректный номер телефона'
                    placeholder='Мобильный телефон'
                    name='phone'
                    className={'ym-disable-keys'}
                />
            </ST.InputsWrap>
            <ST.LegalWrap>
                <PolicyNote
                    buttonText={buttonText}
                    agreeUrl={POLICY_PAGE_LINK}
                />
            </ST.LegalWrap>
            <ST.ButtonContainer>
                <ST.ButtonWrap
                    text={buttonText}
                    type={btnTypes.ACCENT}
                    onClick={onSubmit}
                    disabled={!isValid}
                    isLoading={isLoading}
                />
            </ST.ButtonContainer>
            <PopupResult
                isOpen={popup.isResultOpened}
                onClose={closePopup}
                title={popup.resultTitle}
                text={popup.resultText}
                type={popup.resultType}
            />
        </ST.FormContainer>
    );
};
